import { ResultsView } from '@kijiji/generated/graphql-types'
import dynamic from 'next/dynamic'
import { useTranslation } from 'next-i18next'
import { useTheme } from 'styled-components'

import { ShowAtOrLarger } from '@/components/shared/breakpoint'
import {
  SrpHeaderAndFilterContainer,
  SrpHeaderContainer,
  SrpHeaderSearchTitle,
} from '@/components/srp/header/styled'
import { SaveSearchToggle } from '@/components/srp/save-search'
import { SrpBreadcrumb } from '@/components/srp/srp-breadcrumb/SrpBreadcrumb'
import { LoadingSaveSearchToggle } from '@/components/srp/srp-loading/LoadingSaveSearchToggle'
import { LoadingSrpHeader } from '@/components/srp/srp-loading/LoadingSrpHeader'
import { type IntlResponse, isIntlResponse } from '@/types/translations'
import { Flex } from '@/ui/atoms/flex'
import { Spacing } from '@/ui/atoms/spacing'

const QuickFilters = dynamic(() =>
  import('@/features/filters/components/QuickFilters/QuickFilters').then((mod) => mod.QuickFilters)
)

/**
 * Top of the search page
 */

export type SrpHeaderProps = {
  h1: string | IntlResponse
  currentPage: number
  loading: boolean
  currentView: ResultsView
  categoryId?: number
  isRadialSearch?: boolean
}

export const SrpHeader = ({ h1, currentPage, currentView, loading = true }: SrpHeaderProps) => {
  const { t } = useTranslation(['srp', 'filters'])
  const { colors, spacing } = useTheme()

  const shouldDisplayPageNumber = currentPage > 1

  const h1Text = isIntlResponse(h1) ? t(h1.intl, { ...h1.params }) : h1

  // Determine whether to show SaveSearchToggle
  const showSaveSearchToggle = currentView !== ResultsView.Map

  return (
    <>
      <SrpHeaderAndFilterContainer>
        {loading ? (
          <LoadingSrpHeader />
        ) : (
          <SrpHeaderContainer>
            <SrpBreadcrumb />
            <SrpHeaderSearchTitle
              isMapView={currentView === ResultsView.Map}
              aria-live="polite"
              color={colors.grey.primary}
              data-testid="srp-header-title"
              weight="semiBold"
              size={{ small: 'medium', medium: 'xLarge' }}
            >
              {h1Text}
              {shouldDisplayPageNumber &&
                t('header.results.title.page_number', { pageNumber: currentPage })}
            </SrpHeaderSearchTitle>
          </SrpHeaderContainer>
        )}

        {/*
         * The quick filters should always be available on desktop.
         * It should only show quick filters on mobile if the mweb redesign is enabled
         * AND it is not on maps view.
         **/}
        <QuickFilters />
      </SrpHeaderAndFilterContainer>

      {showSaveSearchToggle ? (
        <Spacing medium={{ mBottom: spacing.default }}>
          {loading ? (
            <LoadingSaveSearchToggle />
          ) : (
            <>
              <Flex justifyContent="space-between">
                <ShowAtOrLarger breakpoint="medium">
                  <SaveSearchToggle />
                </ShowAtOrLarger>
              </Flex>
            </>
          )}
        </Spacing>
      ) : null}
    </>
  )
}
